import { useMutation, useQuery } from '@apollo/client'
import { TIMEZONE_OPTIONS } from 'constants'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useOutletContext } from 'react-router-dom'

import {
  Box,
  Button,
  CardBox,
  Fields,
  FormControl,
  InputLabel,
  MenuItem,
  Row,
  Select,
  Stack,
  Text,
  useAlert,
} from '../../components'
import { validations } from '../../components/form'
import { mutations, queries } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'
import { detectBrowserTimezone } from '../../hooks'

export function UserDetailsForm() {
  const { data: { me } = {} } = useQuery(queries.user.me)
  const {
    setHasUnsavedChanges: trackUnsavedChanges,
    profileFormRef,
    onSubmitSuccess: onSuccess,
    onSubmitFail: onFail,
    donorFundId,
  } = useOutletContext()
  const { email, profile, timezone: userTimezone, isOrganization, corporateName } = me ?? {}
  const fundProfile = me?.funds?.find((f) => f.id === donorFundId)?.fundProfile
  const navigate = useNavigate()

  const { register, handleSubmit, formState, setError, getValues, setValue, watch, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      corporateName: corporateName || fundProfile?.portalName || '',
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      middleInitials: profile?.middleInitials ?? '',
      email: email ?? '',
      phone: profile?.phone ?? '',
      // Match the Select's own fallback chain exactly (see `value` below) --
      // a mismatched fallback (e.g. `||` here vs `??` there) is exactly what
      // let a blur on the Select read back a value that didn't match this
      // snapshot and falsely flip isDirty.
      timezone: userTimezone ?? 'America/Vancouver',
    },
  })
  const { isDirty, isSubmitting, errors = {} } = formState

  const timezone = watch('timezone')
  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  useEffect(() => {
    trackUnsavedChanges(isDirty)
  }, [timezone, isDirty, trackUnsavedChanges])

  const [updateProfile] = useMutation(mutations.user.updateProfile, {
    onCompleted: () => {
      setAlert({ message: 'Profile updated', severity: 'success' })
      reset(getValues())
      onSuccess?.()
    },
    onError: (serverErrors) => {
      parseAndSetFormErrors(serverErrors, setError)
      setAlert({ message: 'Update failed', severity: 'error' })
      onFail?.()
    },
    refetchQueries: [{ query: queries.user.me }],
  })

  const onSubmit = async (data) => {
    // Remove email from data as it's not part of UpdateProfileInput -- the field is
    // display-only here (disabled, changed via the separate Security/Change Email flow)
    const { email: _email, ...profileData } = data
    const submissionData = {
      ...profileData,
      timezone: profileData.timezone === 'auto' ? detectBrowserTimezone() : profileData.timezone,
    }
    updateProfile({ variables: { data: submissionData } })
  }

  const onInvalid = () => {
    onFail?.()
  }

  return (
    <CardBox>
      <Text.H4>User Details</Text.H4>
      <br />
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} ref={profileFormRef}>
        <Stack>
          <Text.H6>Name</Text.H6>
          {isOrganization ? (
            <>
              <Fields.Text
                inputProps={{ 'data-testid': 'organizationName' }}
                label="Organization Name *"
                error={!!errors.corporateName}
                helperText={errors.corporateName?.message}
                defaultValue={corporateName || fundProfile?.portalName}
                {...register('corporateName', { ...validations.required })}
              />
              <Text.H6 sx={{ mt: 2 }}>Primary Contact</Text.H6>
              <Row>
                <Fields.Text
                  inputProps={{ 'data-testid': 'firstName' }}
                  label="First Name *"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  defaultValue={profile?.firstName}
                  {...register('firstName', { ...validations.required })}
                />
                <Fields.Text
                  inputProps={{ 'data-testid': 'lastName' }}
                  label="Last Name *"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  defaultValue={profile?.lastName}
                  {...register('lastName', { ...validations.required })}
                />
              </Row>
              <Fields.Text
                inputProps={{ 'data-testid': 'middleInitials' }}
                label="Middle Initials"
                error={!!errors.middleInitials}
                helperText={errors.middleInitials?.message}
                defaultValue={profile?.middleInitials}
                {...register('middleInitials')}
              />
            </>
          ) : (
            <Row>
              <Fields.Text
                inputProps={{ 'data-testid': 'firstName' }}
                label="First Name *"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                defaultValue={profile?.firstName}
                {...register('firstName', { ...validations.required })}
              />
              <Fields.Text
                inputProps={{ 'data-testid': 'lastName' }}
                label="Last Name *"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                defaultValue={profile?.lastName}
                {...register('lastName', { ...validations.required })}
              />
            </Row>
          )}

          <Text.H6 sx={{ mt: 2 }}>Contact</Text.H6>
          <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <Fields.Text
              inputProps={{
                'data-testid': 'email',
                style: { color: '#333333', WebkitTextFillColor: '#333333' },
              }}
              type="text"
              label="Email"
              defaultValue={email}
              {...register('email')}
              disabled
            />
            <Button
              sx={{
                position: 'absolute',
                right: '1%',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20%',
                maxHeight: '80%',
              }}
              onClick={() => navigate('../security')}
            >
              Change Email
            </Button>
          </Box>

          <Fields.Text
            inputProps={{ 'data-testid': 'phoneNumber' }}
            type="text"
            label="Phone *"
            error={!!errors.phone}
            helperText={errors?.phone?.message}
            defaultValue={profile?.phone}
            {...register('phone', { ...validations.required, ...validations.phoneNumber })}
          />

          <Text.H6 sx={{ mt: 3 }}>Timezone</Text.H6>
          <Text.Body sx={{ mb: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
            Dates and times throughout the portal will be displayed in your selected timezone.
          </Text.Body>
          <FormControl fullWidth>
            <InputLabel id="timezoneLabel">Timezone</InputLabel>
            <Select
              inputProps={{ 'data-testid': 'timezone' }}
              // Trust the already-seeded watched value alone -- re-deriving from
              // userTimezone here too gave this a second, independent source
              // that could (and did) diverge from the useForm() defaultValues
              // snapshot above.
              value={timezone ?? 'America/Vancouver'}
              onChange={(e) => {
                setValue('timezone', e.target.value, { shouldDirty: true })
              }}
              label="Timezone"
              labelId="timezoneLabel"
              aria-label="Timezone"
              defaultValue={userTimezone ?? 'America/Vancouver'}
              {...register('timezone')}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
            <Button type="submit" disabled={isSubmitting} data-testid="updateProfileButton">
              Update Details
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      </form>
    </CardBox>
  )
}
